import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './auth-context'
import makeAuthorizedRequest from '../core/api'

const NotificationContext = createContext(null)

/**
 * NotificationProvider - Manages batch job notifications
 * Only polls for job status updates when there are scheduled batch jobs (>12 images)
 * Normal generation flows (up to 12 images) get immediate responses and don't need polling
 */
export const NotificationProvider = ({ children }) => {
  const { idToken, isAuthenticated } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  // Track if user has scheduled batch jobs that need polling
  const [hasPendingBatchJobs, setHasPendingBatchJobs] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem('hasPendingBatchJobs') === 'true'
  })

  // Called when a batch job is scheduled (from BatchScheduleModal)
  const onBatchJobScheduled = useCallback(() => {
    setHasPendingBatchJobs(true)
    localStorage.setItem('hasPendingBatchJobs', 'true')
  }, [])

  // Fetch notifications from the batch jobs API
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return

    try {
      setIsLoading(true)
      // Get recently completed batch jobs as notifications
      const response = await makeAuthorizedRequest('/batch-jobs?status=completed&limit=10', 'GET', null, idToken)

      if (response && response.jobs) {
        // Transform jobs into notifications
        const jobNotifications = response.jobs.map(job => ({
          id: job.id,
          type: 'batch_complete',
          title: job.name || 'Batch Job Complete',
          message: `${job.completedImages}/${job.totalImages} images generated`,
          createdAt: job.processingCompletedAt || job.updatedAt,
          read: false, // We'll track read status locally for now
          batchJobId: job.id
        }))
        setNotifications(jobNotifications)

        // For now, all are "unread" - you can persist read status later
        const storedReadIds = JSON.parse(localStorage.getItem('readNotifications') || '[]')
        const unread = jobNotifications.filter(n => !storedReadIds.includes(n.id))
        setUnreadCount(unread.length)

        // If no pending/processing jobs found, we can stop polling
        // Check for any jobs that are not yet completed
        const pendingResponse = await makeAuthorizedRequest('/batch-jobs?status=pending&limit=1', 'GET', null, idToken)
        const processingResponse = await makeAuthorizedRequest('/batch-jobs?status=processing&limit=1', 'GET', null, idToken)

        const hasPending = (pendingResponse?.jobs?.length > 0) || (processingResponse?.jobs?.length > 0)
        if (!hasPending) {
          setHasPendingBatchJobs(false)
          localStorage.setItem('hasPendingBatchJobs', 'false')
        }
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err)
    } finally {
      setIsLoading(false)
    }
  }, [idToken, isAuthenticated])

  // Poll for notifications every 60 seconds ONLY if there are pending batch jobs
  useEffect(() => {
    if (!isAuthenticated || !hasPendingBatchJobs) return

    fetchNotifications()
    const interval = setInterval(fetchNotifications, 60000)
    return () => clearInterval(interval)
  }, [isAuthenticated, hasPendingBatchJobs, fetchNotifications])

  // Mark a notification as read
  const markAsRead = useCallback((notificationId) => {
    const storedReadIds = JSON.parse(localStorage.getItem('readNotifications') || '[]')
    if (!storedReadIds.includes(notificationId)) {
      storedReadIds.push(notificationId)
      localStorage.setItem('readNotifications', JSON.stringify(storedReadIds))
      setUnreadCount(prev => Math.max(0, prev - 1))
    }
  }, [])

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    const allIds = notifications.map(n => n.id)
    localStorage.setItem('readNotifications', JSON.stringify(allIds))
    setUnreadCount(0)
  }, [notifications])

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([])
    setUnreadCount(0)
  }, [])

  const value = {
    notifications,
    unreadCount,
    isLoading,
    hasPendingBatchJobs,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    clearAll,
    onBatchJobScheduled // Expose this for BatchScheduleModal to call
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
}

export default NotificationContext

